/**
 * All application-specific configuration should go here. Where possible, all
 * `process.env` refs should be contained to this file.
 *
 * This includes all 'tunable' settings.
 */

import { initAxios } from 'adready-api/init';
import axios from 'axios';
import {
  DEFAULT_DR_LOGO,
  SQUARE_DR_LOGO,
  AI_ASSISTANT_PLATFORM_URL,
  AI_ASSISTANT_REPORT_BUILDER_URL,
  AI_ASSISTANT_MNI_LOGO_URL,
} from './constant';
import { setCssVariable } from './helpers/global/misc-helpers';
import { initSentry } from './init/sentry';
import { formatViamediaName } from './util/utility-functions';
import { resolvedConfig } from './util/shared-config';

// App specific config
const config = {
  // When enabled, allows selecting all target types for all products
  DEBUG_TARGETING: process.env.VUE_APP_DEBUG_TARGETING === '1',

  async init() {
    // Load some configs dynamically
    //
    // This solves the problem of baking a docker image with a static JS build
    // but still change certain config values dynamically based on the
    // environment on app startup.
    //
    // ALL per-ENV vars *MUST* be loaded from this endpoint!
    let appConfigSuccess;
    await axios
      .get('/api/appconfig.json')
      .then((res) => {
        Object.assign(config, res.data);
        config.CONFIG_API_URL ||= process.env.VUE_APP_CONFIG_API_URL;
        config.SENTRY_DSN ||= process.env.SENTRY_DSN;
        config.ENV_NAME ||= process.env.ENV_NAME;
        config.GA_ID ||= process.env.VUE_APP_GA_ID;
        config.TABLEAU_URL ||= process.env.VUE_APP_TABLEAU_URL;
        config.MAX_RUN_REPORT_COUNT ||= process.env.VUE_APP_MAX_RUN_REPORT_COUNT;
        config.MAX_RUN_COUNT ||= process.env.VUE_APP_MAX_RUN_COUNT;
        config.STORAGE_TIME_TO_LIVE_IN_MS ||= process.env.VUE_APP_STORAGE_TIME_TO_LIVE_IN_MS;
        config.RB1_VISIBLE ||= process.env.VUE_APP_RB1_VISIBLE;
        config.RB2_VISIBLE ||= process.env.VUE_APP_RB2_VISIBLE;
        config.RB1_VISIBLE_TO_ADVERTISERS ||= process.env.VUE_APP_RB1_VISIBLE_TO_ADVERTISERS;
        config.AUTHORIZED_DSP_USERS ||= process.env.VUE_APP_AUTHORIZED_DSP_USERS;
        config.GA_TOOL_HIDDEN_FOR_ADVERTISERS ||=
          process.env.VUE_APP_GA_TOOL_HIDDEN_FOR_ADVERTISERS;
        config.AGENCY_REPORTS_VISIBLE_TO_ACCOUNTS ||=
          process.env.VUE_APP_AGENCY_REPORTS_VISIBLE_TO_ACCOUNTS;

        resolvedConfig.MAX_RUN_REPORT_COUNT = config.MAX_RUN_REPORT_COUNT;
        resolvedConfig.MAX_RUN_COUNT = config.MAX_RUN_COUNT;
        resolvedConfig.STORAGE_TIME_TO_LIVE_IN_MS = config.STORAGE_TIME_TO_LIVE_IN_MS;
        resolvedConfig.RB1_VISIBLE = config.RB1_VISIBLE;
        resolvedConfig.RB2_VISIBLE = config.RB2_VISIBLE;
        resolvedConfig.RB1_VISIBLE_TO_ADVERTISERS = config.RB1_VISIBLE_TO_ADVERTISERS;
        resolvedConfig.AUTHORIZED_DSP_USERS = config.AUTHORIZED_DSP_USERS;
        resolvedConfig.GA_TOOL_HIDDEN_FOR_ADVERTISERS = config.GA_TOOL_HIDDEN_FOR_ADVERTISERS;
        resolvedConfig.AGENCY_REPORTS_VISIBLE_TO_ACCOUNTS =
          config.AGENCY_REPORTS_VISIBLE_TO_ACCOUNTS;

        // config.AI_ASSISTANT_FULL_PLATFORM_SCRIPT_URL ||= process.env.AI_ASSISTANT_FULL_PLATFORM_SCRIPT_URL;
        // config.AI_ASSISTANT_REPORT_BUILDER_URL ||= process.env.AI_ASSISTANT_REPORT_BUILDER_URL;
        // config.MNI_LOGO_SCRIPT_URL ||= process.env.MNI_LOGO_SCRIPT_URL;
        appConfigSuccess = true;
      })
      .catch((e) => {
        // local from env in case api fails on local
        appConfigSuccess = false;
        console.error('Failed to load /api/appconfig.json:', e);
        config.CONFIG_API_URL = process.env.VUE_APP_CONFIG_API_URL;
        config.ADREADY_URL = process.env.VUE_APP_ADREADY_URL;
        config.COOKIE_DOMAIN = process.env.VUE_APP_COOKIE_DOMAIN;
        config.SENTRY_DSN = process.env.SENTRY_DSN;
        config.ENV_NAME = process.env.ENV_NAME;
        config.GA_ID = process.env.VUE_APP_GA_ID;
        config.APP_NAME = 'Reporting';
        config.APP_URL = process.env.VUE_APP_CONSOLE_URL;
        config.APP_FAVICON_URL = 'https://storage.googleapis.com/flip-assets/logos/dr-favicon.ico';
        config.APP_LOGIN_THEME = 'theme-default';
        config.FLIP_URL = process.env.VUE_APP_FLIP_URL;
        config.SITE_LOGO_URL = DEFAULT_DR_LOGO;
        config.SQUARE_LOGO_URL = SQUARE_DR_LOGO;
        config.MANAGING_ORG_ID = 1;
        config.TABLEAU_URL = process.env.VUE_APP_TABLEAU_URL;
        config.PENDO_API_KEY = process.env.VUE_APP_CONSOLE_PENDO_API_KEY;
        config.PRIMARY_COLOR = '#006987';
        config.BUTTON_TEXT = 'dark';
        config.GOOGLE_SIGN_IN = true;
        config.MAX_RUN_REPORT_COUNT = process.env.VUE_APP_MAX_RUN_REPORT_COUNT;
        config.MAX_RUN_COUNT = process.env.VUE_APP_MAX_RUN_COUNT;
        config.STORAGE_TIME_TO_LIVE_IN_MS = process.env.VUE_APP_STORAGE_TIME_TO_LIVE_IN_MS;
        config.RB1_VISIBLE = process.env.VUE_APP_RB1_VISIBLE;
        config.RB2_VISIBLE = process.env.VUE_APP_RB2_VISIBLE;
        config.RB1_VISIBLE_TO_ADVERTISERS = process.env.VUE_APP_RB1_VISIBLE_TO_ADVERTISERS;
        config.AUTHORIZED_DSP_USERS = process.env.VUE_APP_AUTHORIZED_DSP_USERS;
        config.GA_TOOL_HIDDEN_FOR_ADVERTISERS = process.env.VUE_APP_GA_TOOL_HIDDEN_FOR_ADVERTISERS;
        config.AGENCY_REPORTS_VISIBLE_TO_ACCOUNTS =
          process.env.VUE_APP_AGENCY_REPORTS_VISIBLE_TO_ACCOUNTS;

        resolvedConfig.MAX_RUN_REPORT_COUNT = config.MAX_RUN_REPORT_COUNT;
        resolvedConfig.MAX_RUN_COUNT = config.MAX_RUN_COUNT;
        resolvedConfig.STORAGE_TIME_TO_LIVE_IN_MS = config.STORAGE_TIME_TO_LIVE_IN_MS;
        resolvedConfig.RB1_VISIBLE = config.RB1_VISIBLE;
        resolvedConfig.RB2_VISIBLE = config.RB2_VISIBLE;
        resolvedConfig.RB1_VISIBLE_TO_ADVERTISERS = config.RB1_VISIBLE_TO_ADVERTISERS;
        resolvedConfig.AUTHORIZED_DSP_USERS = config.AUTHORIZED_DSP_USERS;
        resolvedConfig.GA_TOOL_HIDDEN_FOR_ADVERTISERS = config.GA_TOOL_HIDDEN_FOR_ADVERTISERS;
        resolvedConfig.AGENCY_REPORTS_VISIBLE_TO_ACCOUNTS =
          config.AGENCY_REPORTS_VISIBLE_TO_ACCOUNTS;
        // config.AI_ASSISTANT_FULL_PLATFORM_SCRIPT_URL = process.env.AI_ASSISTANT_FULL_PLATFORM_SCRIPT_URL;
        // config.AI_ASSISTANT_REPORT_BUILDER_URL = process.env.AI_ASSISTANT_REPORT_BUILDER_URL;
        // config.MNI_LOGO_SCRIPT_URL = process.env.MNI_LOGO_SCRIPT_URL;
        const apiURL = `${config.ADREADY_URL}/api`;
        initAxios(apiURL, config);
        initSentry(config.SENTRY_DSN, config.ENV_NAME);
      });

    if (!appConfigSuccess) {
      return config;
    }
    const configApiUrl = `${config.CONFIG_API_URL}/api/config?domain=${window.location.hostname}`;
    return axios
      .get(configApiUrl, { withCredentials: true })
      .then(async (res) => {
        const serverConfig = res.data;
        config.ADREADY_URL = serverConfig.params.apiUrl;
        config.COOKIE_DOMAIN = serverConfig.params.domain;
        config.APP_NAME = serverConfig.params.name;
        config.APP_URL = serverConfig.params.consoleUrl;
        config.SITE_LOGO_URL = serverConfig.params.siteLogoUrl;
        config.SQUARE_LOGO_URL = serverConfig.params.squareIconUrl;
        config.GOOGLE_SIGN_IN = serverConfig.params.googleSignIn === '1';
        config.APP_FAVICON_URL = serverConfig.params.favIconUrl;
        config.PRIMARY_COLOR = serverConfig.theme.primaryColor;
        config.BUTTON_TEXT = serverConfig.theme.buttonText;
        config.FLIP_URL = serverConfig.params.flipUrl;
        config.PENDO_API_KEY = serverConfig.appConsolePendoApiKey;
        config.ZIPLYNE_URL = serverConfig.ziplyneUrl;
        config.ZIPLYNE_DELAY = serverConfig.ziplyneDelay;
        const apiURL = `${config.ADREADY_URL}/api`;
        setCssVariable(config);
        initAxios(apiURL, config);
        initSentry(config.SENTRY_DSN, config.ENV_NAME);
      })
      .catch((e) => {
        // local from env in case api fails on local
        console.error(`Failed to load ${configApiUrl}`, e);
      });
  },
  loadPendoLib() {
    if (!config?.pendoEnabled?.()) {
      console.log('Pendo credentials not found for App Console');
      return;
    }
    (function pendoFunction1(apiKey) {
      (function pendoFunction2(p, e, n, d, o) {
        let w;
        let x;
        p[d] = p[d] || {};
        o = p[d];
        o._q = o._q || [];
        const v = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track'];
        for (w = 0, x = v.length; w < x; ++w)
          (function pendoFunction3(m) {
            o[m] =
              o[m] ||
              function pendoFunction4(...args) {
                o._q[m === v[0] ? 'unshift' : 'push']([m].concat([].slice.call(args, 0)));
              };
          })(v[w]);
        const y = e.createElement(n);
        y.async = !0;
        y.src = `https://cdn.pendo.io/agent/static/${apiKey}/pendo.js`;
        const z = e.getElementsByTagName(n)[0];
        z.parentNode.insertBefore(y, z);
      })(window, document, 'script', 'pendo');
    })(config.PENDO_API_KEY);
  },
  initializePendo(userId, userEmail, userOrgId) {
    if (!config?.pendoEnabled?.()) {
      console.log('Pendo not enabled for App Console');
      return;
    }
    try {
      // clear session to forget previously logged in user's id
      window?.pendo?.clearSession();

      const theEnv = config.ENV_NAME ? config.ENV_NAME : 'Unknown';
      const options = {
        visitor: {
          env: theEnv,
          id: userId,
          email: userEmail,
        },
        account: {
          id: userOrgId,
        },
      };
      window?.pendo?.initialize(options);
    } catch (error) {
      console.error('Error clearing Pendo:', error);
    }
  },
  initializeAnonymousPendo() {
    config?.loadPendoLib?.();
    config?.initializePendo?.();
  },
  gaEnabled() {
    return this.GA_ID && this.GA_ID !== 'undefined';
  },
  getAIAssistantPlatformScriptURL() {
    // to be changed
    return AI_ASSISTANT_PLATFORM_URL; // this.AI_ASSISTANT_FULL_PLATFORM_SCRIPT_URL;
  },
  getAIAssistantReportBuilderScriptURL() {
    return AI_ASSISTANT_REPORT_BUILDER_URL; // this.AI_ASSISTANT_REPORT_BUILDER_URL;
  },
  getMNILogoScript() {
    return AI_ASSISTANT_MNI_LOGO_URL; // this.MNI_LOGO_SCRIPT_URL;
  },
  getMaxRunReportCount() {
    return this.MAX_RUN_REPORT_COUNT;
  },
  getMaxRunCount() {
    return this.MAX_RUN_COUNT;
  },
  getStorageTimeToLiveInMs() {
    return this.STORAGE_TIME_TO_LIVE_IN_MS;
  },
  pendoEnabled() {
    return this.PENDO_API_KEY && this.PENDO_API_KEY !== 'undefined';
  },
  adBlockerCheckEnabled() {
    return false;
  },
  isZiplyneEnabled() {
    return this.ZIPLYNE_URL && this.ZIPLYNE_URL !== 'undefined';
  },
  loadZiplyne() {
    (function ziplyneFunction(ziplyneUrl) {
      console.log('Loading Ziplyne...');
      if (!config?.isZiplyneEnabled?.()) {
        console.log('Ziplyne URL not found. Ziplyne will be disabled in this environment');
        return;
      }
      (function addScriptTag(win, doc, tag) {
        const newElement = doc.createElement(tag);
        newElement.async = !0;
        newElement.src = ziplyneUrl;
        const firstScriptTag = doc.getElementsByTagName(tag)[0];
        firstScriptTag.parentNode.insertBefore(newElement, firstScriptTag);
      })(window, document, 'script');
    })(config.ZIPLYNE_URL);
  },
  ziplyneLogin(email, firstName, lastName, currentAdvertiser, allAdvertisers, currentAgency) {
    console.log('Calling ziplyneLogin...');
    if (!config?.isZiplyneEnabled?.()) {
      console.log('Ziplyne disabled in this environment');
      return;
    }

    const viaAdvertiser = allAdvertisers.find((item) => item.name === 'Viamedia');
    if (viaAdvertiser && viaAdvertiser.name)
      viaAdvertiser.name = formatViamediaName(viaAdvertiser.name);

    const data = {
      email,
      first_name: firstName,
      last_name: lastName,
      current_advertiser: currentAdvertiser,
      all_advertisers: allAdvertisers,
      current_agency: formatViamediaName(currentAgency),
    };
    console.log('ziplyne login data', data);
    try {
      if (config.ZIPLYNE_DELAY && parseInt(config.ZIPLYNE_DELAY, 10) > 0) {
        setTimeout(() => {
          try {
            // eslint-disable-next-line no-undef
            $ziplyne.loginUser(data);
          } catch (error) {
            console.error('Error while calling $ziplyne.loginUser:', error);
          }
        }, parseInt(config.ZIPLYNE_DELAY, 10));
      } else {
        // eslint-disable-next-line no-undef
        $ziplyne.loginUser(data);
      }
    } catch (error) {
      console.error('Error while calling $ziplyne.loginUser:', error);
    }
  },
  ziplyneChangeAdvertiser(email, advertiser, currentAgency) {
    console.log('Ziplyne changing advertiser...');
    if (!config?.isZiplyneEnabled?.()) {
      console.log('Ziplyne disabled in this environment');
      return;
    }
    const data = {
      email,
      current_advertiser: advertiser,
      current_agency: formatViamediaName(currentAgency),
    };
    console.log('ziplyne change advertiser data', data);
    try {
      if (config.ZIPLYNE_DELAY && parseInt(config.ZIPLYNE_DELAY, 10) > 0) {
        setTimeout(() => {
          try {
            // eslint-disable-next-line no-undef
            $ziplyne.changeAdvertiser(data);
          } catch (error) {
            console.error('Error while calling $ziplyne.changeAdvertiser:', error);
          }
        }, parseInt(config.ZIPLYNE_DELAY, 10));
      } else {
        // eslint-disable-next-line no-undef
        $ziplyne.changeAdvertiser(data);
      }
    } catch (error) {
      console.error('Error while calling $ziplyne.changeAdvertiser:', error);
    }
  },
};

export default config;
